import React from "react";
import {
    type LucideIcon,
    SparklesIcon,
    BotIcon,
    BrainIcon,
    ZapIcon,
    TargetIcon,
    RocketIcon,
    StarIcon,
    CodeIcon,
    FileTextIcon,
    MessageSquareIcon,
    LightbulbIcon,
    Wand2Icon,
    CpuIcon,
    LayersIcon,
    GitBranchIcon,
    SearchIcon,
    BadgePlusIcon,
    BoxIcon,
    HammerIcon,
    WrenchIcon,
    ShredderIcon,
    Table2Icon,
    PresentationIcon,
    FlaskConicalIcon,
    PocketKnifeIcon,
    ClipboardIcon,
    BadgeQuestionMark,
    BirdIcon,
    SquirrelIcon,
    BugIcon,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

// Define available icons with their names
export const AVAILABLE_ICONS = {
    Sparkles: SparklesIcon,
    Bot: BotIcon,
    Brain: BrainIcon,
    Zap: ZapIcon,
    Target: TargetIcon,
    Rocket: RocketIcon,
    Star: StarIcon,
    Code: CodeIcon,
    File: FileTextIcon,
    Bug: BugIcon,
    Message: MessageSquareIcon,
    Lightbulb: LightbulbIcon,
    Wand: Wand2Icon,
    Cpu: CpuIcon,
    Layers: LayersIcon,
    Branch: GitBranchIcon,
    Search: SearchIcon,
    New: BadgePlusIcon,
    Box: BoxIcon,
    Hammer: HammerIcon,
    Wrench: WrenchIcon,
    Shredder: ShredderIcon,
    Table: Table2Icon,
    Presentation: PresentationIcon,
    Flask: FlaskConicalIcon,
    Knife: PocketKnifeIcon,
    Clipboard: ClipboardIcon,
    Question: BadgeQuestionMark,
    Bird: BirdIcon,
    Squirrel: SquirrelIcon,
} as const;

export type IconName = keyof typeof AVAILABLE_ICONS;

interface IconPickerProps {
    value?: string;
    onValueChange: (value: string) => void;
    disabled?: boolean;
}

export function IconPicker({ value, onValueChange, disabled }: IconPickerProps) {
    const selectedIcon = value && value in AVAILABLE_ICONS ? (value as IconName) : undefined;
    const SelectedIconComponent = getIconComponent(selectedIcon);

    return (
        <Select value={value} onValueChange={onValueChange} disabled={disabled}>
            <SelectTrigger className="w-full">
                <SelectValue>
                    {selectedIcon && (
                        <div className="flex items-center gap-2">
                            <SelectedIconComponent className="h-4 w-4" />
                            {selectedIcon}
                        </div>
                    )}
                </SelectValue>
                {!selectedIcon && (
                    <div className="flex w-full flex-row items-center justify-start text-muted-foreground">
                        Select an icon
                    </div>
                )}
            </SelectTrigger>
            <SelectContent scrollButtons={"hide"}>
                {Object.entries(AVAILABLE_ICONS).map(([name, IconComponent]) => (
                    <SelectItem key={name} value={name}>
                        <div className="flex items-center gap-2">
                            <IconComponent className="h-4 w-4" />
                            <span>{name}</span>
                        </div>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

// Helper function to get icon component by name
export function getIconComponent(iconName?: string): LucideIcon {
    if (!iconName || !(iconName in AVAILABLE_ICONS)) {
        return SparklesIcon; // Default fallback
    }
    return AVAILABLE_ICONS[iconName as IconName];
}
